import type { FC } from 'react';
import {
  ClockIcon,
  InformationCircleIcon,
  PauseCircleIcon,
} from '@heroicons/react/24/outline';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import HighchartsMore from 'highcharts/highcharts-more';
import HighchartsGauge from 'highcharts/modules/solid-gauge';

import { cls } from '@/utils/cls';
import type { GaugeMetricData } from '@/warranty/domain/entities/gauge-metric';

import GaugeInfoCard from './gauge-info-card';
import { getGaugeLayout } from './get-gauge-layout';
import { getGaugeSeries } from './get-gauge-series';

// Initialize Highcharts modules
if (typeof Highcharts === 'object') {
  HighchartsMore(Highcharts);
  HighchartsGauge(Highcharts);
}

export const GaugeCard: FC<{ metric: GaugeMetricData; className?: string }> = ({
  metric,
  className = '',
}) => {
  const isActive = metric.state === 'active';
  const isDevelopment = metric.state === 'development';
  const isWrongLevel = metric.state === 'wrong_level';

  const baseOptions = getGaugeLayout(metric);

  const seriesOptions = isActive
    ? getGaugeSeries(metric)
    : [
        {
          type: 'gauge',
          data: [],
          dial: { radius: '0%' },
          pivot: { radius: 0 },
          dataLabels: { enabled: false },
          tooltip: { enabled: false },
        },
      ];

  const chartOptions = {
    ...baseOptions,
    series: seriesOptions,
    chart: {
      ...baseOptions.chart,
      animation: Boolean(isActive),
    },
  };

  return (
    <div className={cls('group flex w-full flex-col items-center', className)}>
      <div className="relative aspect-[2/1] max-h-56 w-full md:max-h-64">
        <HighchartsReact highcharts={Highcharts} options={chartOptions} />

        {!isActive && (
          <div className="absolute inset-0 flex items-center justify-center bg-white/20 backdrop-blur-sm">
            {isDevelopment ? (
              <div className="flex items-center gap-2 text-neutral-600">
                <ClockIcon className="size-6" />
                <span className="text-lg font-semibold">Coming Soon</span>
              </div>
            ) : isWrongLevel ? (
              <div className="flex items-center gap-2 text-neutral-600">
                <InformationCircleIcon className="size-6" />
                <span className="text-lg font-semibold">
                  Not available on this level
                </span>
              </div>
            ) : (
              <div className="flex items-center gap-2 text-neutral-600">
                <PauseCircleIcon className="size-6" />
                <span className="text-lg font-semibold">Inactive</span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Labels */}
      <span
        className={cls(
          'mt-1 text-center text-base font-semibold tracking-wide',
          !isActive && 'text-gray-400',
        )}
      >
        {metric.id}
      </span>
      <span className={cls('mb-2 text-sm', !isActive && 'text-gray-400')}>
        {metric.longName}
      </span>

      <GaugeInfoCard metric={metric} placement="bottom" />
    </div>
  );
};
